import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import { CheckedFormElement } from '../checked-form-element';
import css from './lib-radio.css';

/** A radio option whose selection is shared with its containing group. */
@customElement('lib-radio')
export class LibRadio extends CheckedFormElement {
  protected override get hostFocusSelector(): string { return 'input'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  @property({ type: Boolean, reflect: true }) error = false;
  @property({ type: Boolean }) bold = false;
  @property() value = '';
  protected override get submissionValue(): string { return this.value; }
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;

  override render() {
    return html`<label class="lib-radio ${this.effectivelyDisabled ? 'lib-radio-disabled' : ''} ${this.error ? 'lib-radio-error' : ''} ${this.bold ? 'lib-radio-bold' : ''}">
      <input class="lib-radio-input" type="radio" name=${this.name} value=${this.value}
        .checked=${this.checked} ?disabled=${this.effectivelyDisabled}
        aria-invalid=${this.error ? 'true' : nothing} aria-label=${this.ariaLabel || nothing}
        @change=${this.#change}>
      <span class="lib-radio-circle" aria-hidden="true"></span>
      <span class="lib-radio-label"><slot></slot></span>
    </label>`;
  }

  /** Selects this enabled option and reports its value. */
  select(): void {
    if (this.effectivelyDisabled) return;
    this.setSelected(true);
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  /** Updates selection and submission together without reporting a user change. */
  setSelected(checked: boolean): void {
    this.checked = checked;
    this.syncFormValue();
  }

  protected override notifyReset(): void {
    if (this.closest('lib-radio-group'))
      this.dispatchEvent(new Event('lib-radio-reset', { bubbles: true, composed: true }));
    else super.notifyReset();
  }

  #change(event: Event): void {
    event.stopPropagation();
    this.select();
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-radio': LibRadio; }
}
